import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { ReturnsAndClaimsPermissions } from "@redotech/redo-model/permissions";
import { RecycleSystemView } from "@redotech/redo-model/views/default-return-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  OrderManagementSuiteStatusesContext,
  RECYCLE_ICON,
  RECYCLE_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { RecycleViewOrderingContext } from "../../../../services/recycle/recycle-view-ordering-service";
import { useViewFolderLogic } from "./use-view-folder-logic";
import {
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

const RecycleItemContent = memo(function RecycleItemContent() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const recycleViewOrderingContext = useRequiredContext(
    RecycleViewOrderingContext,
  );

  const allViews = useMemo(() => {
    return [...recycleViewOrderingContext.views].filter(
      (view) => view.name !== RecycleSystemView.CREATE,
    );
  }, [recycleViewOrderingContext]);

  const allFolders = useMemo(() => {
    return [...recycleViewOrderingContext.folders];
  }, [recycleViewOrderingContext]);

  const notificationCounts = recycleViewOrderingContext.notificationCounts;

  const {
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  } = useViewFolderLogic<"recycle">({
    allViews,
    allFolders,
    notificationCounts,
  });

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix: routes.recycleHref,
      viewCounts: notificationCounts,
      folderCounts: folderNotificationCounts,
      hideZeroNotifications: false,
    });
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    routes.recycleHref,
    notificationCounts,
    folderNotificationCounts,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    recycleViewOrderingContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"recycle">(
      flattenedItems,
      allViews,
      allFolders,
    );
    recycleViewOrderingContext.updateOrdering(views, folders);
  });

  const recycleMatch = useMatch(`${routes.getRecycleHref()}/*`);
  const anyChildSelected = !!recycleMatch;

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getRecycleHref() }}
      anyChildSelected={anyChildSelected}
      FilledIcon={RECYCLE_ICON_FILLED}
      friendlyName="Recycle"
      id="recycle"
      notificationCount={totalNotificationCount}
      UnfilledIcon={RECYCLE_ICON}
    >
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getRecycleSubRoute("all-recycle"),
        }}
        friendlyName="All recycle"
        id="all-recycle"
      />
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getRecycleSubRoute("create") }}
        friendlyName="+ Add new view"
        id="orders-recycle-create"
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            recycleViewOrderingContext.openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="orders-recycle-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});

export const RecycleItem = memo(function RecycleItem() {
  const hasPermissionToViewItem = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS,
  );
  const { recycleEnabled } = useContext(OrderManagementSuiteStatusesContext);

  if (!hasPermissionToViewItem || !recycleEnabled) {
    return null;
  }

  return <RecycleItemContent />;
});
